import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { TeamChartLegendItem } from "@/components/team/team-chart-legend-item";
import { useTranslation } from "@/hooks/useTranslation";
import { ApiUsageDayDTO } from "@shaarp/common";
import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
} from "recharts";
import { format, parseISO } from "date-fns";
import { fr, enUS } from "date-fns/locale";

const ACTION_COLORS: Record<string, string> = {
  GET_COMPANY: "hsl(250, 97%, 62%)",
  SEARCH_COMPANIES: "hsl(210, 90%, 55%)",
  AI_SEARCH_COMPANIES: "hsl(280, 85%, 55%)",
  GET_COMPANY_SIMILARS: "hsl(170, 75%, 45%)",
  GET_COMPANY_EMPLOYEES: "hsl(35, 90%, 55%)",
  REVEAL_EMPLOYEE_EMAIL: "hsl(340, 80%, 55%)",
  REVEAL_EMPLOYEE_PHONE: "hsl(15, 85%, 55%)",
  GET_COMPANY_FULL: "hsl(260, 80%, 50%)",
  GET_CREDITS_BALANCE: "hsl(200, 60%, 50%)",
};

type ApiUsageChartProps = {
  data: ApiUsageDayDTO[];
  isLoading: boolean;
};

export function ApiUsageChart({ data, isLoading }: ApiUsageChartProps) {
  const { t, currentLanguage } = useTranslation();

  const actions = useMemo(() => {
    const set = new Set<string>();
    for (const entry of data) {
      set.add(entry.action);
    }
    return Array.from(set);
  }, [data]);

  const chartData = useMemo(() => {
    const dayMap = new Map<string, Record<string, string | number>>();

    for (const entry of data) {
      if (!dayMap.has(entry.day)) {
        dayMap.set(entry.day, {
          day: format(
            parseISO(entry.day),
            currentLanguage === "fr" ? "dd/MM" : "MM/dd",
            { locale: currentLanguage === "fr" ? fr : enUS }
          ),
        });
      }
      const dayEntry = dayMap.get(entry.day)!;
      dayEntry[entry.action] = entry.requests;
    }

    // Calculate totals
    for (const [, dayEntry] of dayMap) {
      let total = 0;
      for (const action of actions) {
        total += (dayEntry[action] as number) || 0;
      }
      dayEntry.total = total;
    }

    return Array.from(dayMap.values());
  }, [data, actions, currentLanguage]);

  const chartConfig = useMemo(() => {
    const config: ChartConfig = {};
    for (const action of actions) {
      config[action] = {
        label: t(`team.apiKeys.usage.actions.${action}`, {
          defaultValue: action,
        }),
        color: ACTION_COLORS[action] ?? "hsl(0, 0%, 60%)",
      };
    }
    return config;
  }, [actions, t]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[250px]">
        <div className="animate-pulse text-muted-foreground">
          {t("common.loading")}
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[250px]">
        <p className="text-muted-foreground">
          {t("team.apiKeys.usage.noData")}
        </p>
      </div>
    );
  }

  return (
    <>
      <ChartContainer config={chartConfig} className="h-[250px] w-full">
        <BarChart data={chartData} accessibilityLayer>
          <CartesianGrid vertical={false} syncWithTicks={true} />
          <XAxis
            dataKey="day"
            tickLine={false}
            tickMargin={10}
            axisLine={false}
          />
          <YAxis
            tickLine={true}
            axisLine={false}
            tickMargin={8}
            padding={{ top: 10 }}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          {actions.map((action, index) => {
            const isLast = index === actions.length - 1;
            const color = ACTION_COLORS[action] ?? "hsl(0, 0%, 60%)";
            return (
              <Bar
                key={action}
                dataKey={action}
                stackId="a"
                fill={color}
                radius={isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              >
                {isLast && (
                  <LabelList
                    dataKey="total"
                    position="top"
                    fontSize={12}
                    fontWeight={500}
                  />
                )}
              </Bar>
            );
          })}
        </BarChart>
      </ChartContainer>
      <ul className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-1.5 text-xs">
        {actions.map((action) => (
          <li key={action}>
            <TeamChartLegendItem
              color={ACTION_COLORS[action] ?? "hsl(0, 0%, 60%)"}
              name={t(`team.apiKeys.usage.actions.${action}`, {
                defaultValue: action,
              })}
            />
          </li>
        ))}
      </ul>
    </>
  );
}
